import { useTranslation } from "react-i18next";
import { usePostDischarge } from "../dashboard/post-discharge/usePostDischarge";
import UiFieldMessage from "../../../components/field-message/FieldMessage.component";
import { FieldMessageSeverity } from "../../../models/components/field-message.model";
import { useState } from "react";
import { PostDischargeInlineUpdateForm } from "../../../models/pages/post-discharge-modal/post-discharge-update.model";
import {
	PostDischargeProcedureCreateRequest,
	ProcedureStatus,
} from "../../../models/procedures.model";
import { careTransitionTypeOptions } from "../../../utils/care-transitions";
import { EndpointsService } from "../../../services/endpoints/endpoints.service";
import { FieldError } from "../../../models/form.model";
import { Validations } from "../../../services/form/validations.service";
import FormSection from "../../../components/form-section/FormSection.component";
import UiSelect from "../../../components/select/Select.component";
import { ListOption } from "../../../models/misc.model";
import { t } from "i18next";
import { Form } from "react-final-form";
import UiInputTextarea from "../../../components/input-textarea/InputTextarea.component";
import UiButton from "../../../components/button/Button";
import UiInputDate from "../../../components/input-date/InputDate.component";

const PostDischargeCreate = ({
	postDischargeQuery,
	participantId,
	onCancel,
	onComplete,
}: {
	postDischargeQuery: ReturnType<typeof usePostDischarge>;
	participantId: number;
	onCancel: () => void;
	onComplete: () => void;
}) => {
	// Loading
	if (postDischargeQuery.isLoading) {
		return (
			<div className="post-discharge-create-form">
				<div className="message-content">
					<UiFieldMessage
						severity={FieldMessageSeverity.LOADING}
						label="PARTICIPANT.POST_DISCHARGE.MESSAGES.LOADING"
					/>
				</div>
			</div>
		);
	}

	// Error
	if (postDischargeQuery.isError) {
		return (
			<div className="post-discharge-create-form">
				<div className="message-content">
					<UiFieldMessage
						severity={FieldMessageSeverity.DANGER}
						label="PARTICIPANT.POST_DISCHARGE.MESSAGES.ERROR"
					/>
				</div>
			</div>
		);
	}

	/* There is data, or the data is empty */

	return (
		<PostDischargeCreateForm
			participantId={participantId}
			onCancel={onCancel}
			onComplete={onComplete}
		/>
	);
};
export default PostDischargeCreate;

const PostDischargeCreateForm = ({
	participantId,
	onCancel,
	onComplete,
}: {
	participantId: number;
	onCancel: () => void;
	onComplete: () => void;
}) => {
	const { t } = useTranslation("common");
	const [formError, setFormError] = useState<string | null>(null);

	const isValidForm = (): boolean => {
		if (!formData.dateOn || !formData.type || !formData.status) {
			return false;
		}
		return true;
	};

	const [formData, setFormData] = useState<PostDischargeInlineUpdateForm>({
		dateOn: new Date(),
		status: undefined,
		notes: null,
		type: undefined,
	});

	const handleSubmit = async () => {
		const body: PostDischargeProcedureCreateRequest = {
			body: {
				participantId,
				dateOn: formData.dateOn
					? new Date(formData.dateOn).toISOString()
					: new Date().toISOString(),
				type: formData.type!,
				status: formData.status!,
				notes: formData.notes || null,
			},
		};

		setFormError(null);

		await EndpointsService.dataRetriever
			.createPostDischargeProcedure(body)
			.then(() => onComplete())
			.catch((error) => setFormError("PARTICIPANT.POST_DISCHARGE.MESSAGES.ERROR"));
	};

	return (
		<Form
			onSubmit={handleSubmit}
			initialValues={formData}
			render={({ handleSubmit, form, submitting }) => (
				<form onSubmit={handleSubmit}>
					{/* Add form list */}
					<div
						key={"post-discharge-create-form"}
						className="post-discharge-create-form"
					>
						<FormSection>
							<div className="row">
								<div className="col-12 col-md-3">
									<UiInputDate
										id="dateOn"
										placeholder="PARTICIPANT.POST_DISCHARGE.MODAL.dateOn"
										label="PARTICIPANT.POST_DISCHARGE.MODAL.dateOn"
										name="dateOn"
										onChange={(e) =>
											setFormData({
												...formData,
												dateOn: (e.target.value as Date) || null,
											})
										}
										validations={[Validations.required]}
										disabled={submitting}
										showTime
										showButtonBar={false}
									/>
								</div>
							</div>
							<div className="row">
								<div className="col-12 col-md-8 col-lg-4 col-xxl-3">
									<UiSelect
										id={`type`}
										label={"PARTICIPANT.POST_DISCHARGE.MODAL.type"}
										name={`type`}
										onChange={(e: any) => {
											const updatedForm = { ...formData };
											updatedForm.type = e.target.value;
											setFormData(updatedForm);
										}}
										editable={true}
										options={careTransitionTypeOptions().map(
											(option) => new ListOption(option)
										)}
										disabled={submitting}
									/>
								</div>
								<div className="col-12 col-md-8 col-lg-4 col-xxl-3">
									<UiSelect
										id={`status`}
										label="PARTICIPANT.POST_DISCHARGE.MODAL.status"
										name={`status`}
										onChange={(e: any) => {
											const updatedForm = { ...formData };
											updatedForm.status = e.target.value;
											setFormData(updatedForm);
										}}
										editable={true}
										options={[
											new ListOption({
												id: ProcedureStatus.SCHEDULED,
												label: t("ENUMS.PROCEDURES_STATUS.SCHEDULED"),
											}),
											new ListOption({
												id: ProcedureStatus.COMPLETED,
												label: t("ENUMS.PROCEDURES_STATUS.COMPLETED"),
											}),
										]}
										disabled={submitting}
									/>
								</div>
								<div className="col-12 col-md-12 col-lg-8 col-xxl-6">
									<UiInputTextarea
										id={`notes`}
										label="PARTICIPANT.POST_DISCHARGE.MODAL.reason"
										name={`notes`}
										onChange={(e: any) => {
											const updatedForm = { ...formData };
											updatedForm.notes = e.target.value;
											setFormData(updatedForm);
										}}
										disabled={submitting}
									/>
								</div>
							</div>
						</FormSection>

						<div className="action-buttons">
							<UiButton
								label="UI_COMPONENTS.BUTTONS.CANCEL"
								type="button"
								className="p-button-outlined p-button-rounded"
								onClick={onCancel}
								disabled={submitting}
							/>
							<UiButton
								label={
									submitting
										? "UI_COMPONENTS.BUTTONS.SAVING"
										: "UI_COMPONENTS.BUTTONS.SAVE"
								}
								type="submit"
								className="p-button-rounded"
								loading={submitting}
								disabled={submitting || !isValidForm()}
							/>
						</div>
					</div>

					{formError && (
						<div className="form-message">
							<UiFieldMessage
								severity={FieldMessageSeverity.DANGER}
								label={formError}
							/>
						</div>
					)}
				</form>
			)}
		/>
	);
};
